import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { tokenKeys } from '../queries/keys';
import { tokenPaths } from '../queries/paths';

// TSS vite/03 — server state for `api_tokens`.
//
// THERE IS NO UPDATE, and the API is right not to publish one: a token is a
// credential, and editing what a credential can do after handing it out means
// the copy somebody already has quietly changes meaning. It is revoked and
// another one is issued. So this feature reads, issues and revokes — three
// verbs, and no form for the fourth.

export const TOKENS_PAGE_SIZE = 25;

export function useTokensList({ limit = TOKENS_PAGE_SIZE, offset = 0, userId, kind, repositoryId } = {}) {
    const params = { limit, offset };
    if (userId) params.user_id = userId;
    if (kind) params.kind = kind;
    if (repositoryId) params.repository_id = repositoryId;

    return useQuery({
        queryKey: tokenKeys.list(params),
        queryFn: ({ signal }) => api.get(tokenPaths.list(), { params, signal }).then(r => r.data),
        placeholderData: keepPreviousData,
    });
}

// THE ONLY TIME THE TOKEN EXISTS IN CLEAR. The backend does not store it and
// cannot show it again — what it keeps is an HMAC of it plus the prefix. The
// plaintext lives in this mutation's result and in the component state that
// renders it, and nowhere else: not in localStorage, not in the query cache
// (which is why this does NOT write it back with setQueryData), not in a log.
//
// TSS vite/05 forbids stashing the SESSION token anywhere JS can read. This is
// a different credential, but the same reasoning applies harder: this one is
// long-lived and belongs to somebody else.
export function useIssueToken() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (payload) => api.post(tokenPaths.list(), payload).then(r => r.data),
        onSuccess: () => {
            client.invalidateQueries({ queryKey: tokenKeys.all });
            // The pulse's banner and the board's cards read the token list from
            // the shared lookups. Without this, a token issued here shows up on
            // those screens ten minutes later, when the catalog goes stale.
            client.invalidateQueries({ queryKey: ['lookups', 'tokens'] });
        },
    });
}

export function useRevokeToken() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (id) => api.delete(tokenPaths.detail(id)).then(r => r.data),
        onSuccess: () => {
            client.invalidateQueries({ queryKey: tokenKeys.all });
            client.invalidateQueries({ queryKey: ['lookups', 'tokens'] });
        },
    });
}

// The two searches the issue form drives. They live here because the feature
// owns the endpoints it reads (CLAUDE.md), and importing another feature's
// search would be the cross-feature coupling vite/01 forbids.
export function searchUsers(q, signal) {
    return api
        .get(tokenPaths.users(), { params: { limit: 20, offset: 0, q }, signal })
        .then(r => r.data?.items ?? []);
}

export function searchProjects(q, signal) {
    return api
        .get(tokenPaths.projects(), { params: { limit: 20, offset: 0, q }, signal })
        .then(r => r.data?.items ?? []);
}

// The repositories of one project. `/admin/repositories` takes no `?q=`, so
// this is not a search: it is the whole list of a project, which is a handful.
export function useProjectRepositories(projectId) {
    return useQuery({
        queryKey: tokenKeys.repositoriesOf(projectId),
        queryFn: ({ signal }) => api
            .get(tokenPaths.repositories(), {
                params: { limit: 100, offset: 0, project_id: projectId },
                signal,
            })
            .then(r => r.data?.items ?? []),
        enabled: !!projectId,
        staleTime: 5 * 60_000,
    });
}
